import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Avatar, Tooltip } from "@fluentui/react-components";
import { Spinner } from "react-bootstrap";
import Table from "../../components/controls/tables/Table";
import Modal from "../../components/controls/modal/Modal";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import UserSelect from "../../components/userSelect/UserSelect";
import HoverCard from "../../components/hoverCard/HoverCard";
import { generalActions } from "../../store/actions";
import reshuffleservice from "../../store/services/reshuffleRequests.service";
import searchService from "../../store/services/search.service";
import {
    calculatePageSize,
    getEditPermission,
    formatMonthYear,
    getReshuffleStatusLabel,
    UserSearchRefType,
} from "../../helpers/utils";
import ReshuffleSummaryPreview from "./ReshuffleSummaryPreview";
import styles from "./ReshuffleRequest.module.scss";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import Input from "../../components/controls/input/customInput/CustomInput";
import Searchable from "../../components/controls/select/CustomSelect";
import _, { debounce, has } from "lodash";
import moment from "moment";
import { Check, TrendingDown, TrendingUp, X } from "lucide-react";
import { getAccountCurrency, displayDecimalFormatted } from "../../utils/systemSettings";
import { ArrowLeft } from "../../modules/default-svg";
import SafeHtml from "../../components/safeHtml/SafeHtml";
import Skeleton from "react-loading-skeleton";

const now = moment();
const currentYear = now.year();
const currentMonth = now.month() + 1;
const setting = JSON.parse(localStorage.getItem("settings"));

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const YEAR_OPTIONS = Array.from({ length: 15 }, (_, i) => currentYear - 5 + i);


const ReshuffleRequest = ({
    projectID,
    allReshuffleRequests,
    pagination,
    loading,
    pageLoading,
    getReshuffleRequests,
    createReshuffleRequest,
    updateReshuffleRequest,
    deleteReshuffleRequest,
    submitReshuffleRequest,
    approveReshuffleRequest,
    rejectReshuffleRequest,
    executeReshuffleRequest,
    cancelReshuffleRequest,
}) => {
    const dispatch = useDispatch();
    const canEdit = getEditPermission("Project");

    // ── Table data & pagination ────────────────────────────────────────────────────────
    const [data, setData] = useState([]);
    const [perPageItems] = useState(calculatePageSize());
    const [pageNum, setPageNum] = useState(1);
    const [tableSearch, setTableSearch] = useState(null);
    const [deleteId, setDeletedId] = useState(null);

    // ── Modal / wizard state ────────────────────────────────────────────────────
    // modalMode drives what formBody() renders (inside Table.jsx's modal)
    const [modalMode, setModalMode] = useState(""); // '' | 'create' | 'edit'
    const [wizardStep, setWizardStep] = useState(1);      // 1 | 2 | 3 (create only)
    const [selectedRowId, setSelectedRowId] = useState(null);

    // Direct modal (view / approve / reject / summary — outside Table.jsx modal)
    const [showDirectModal, setShowDirectModal] = useState(false);
    const [directModalMode, setDirectModalMode] = useState("view"); // 'view'|'approve'|'reject'|'summary'
    const [directModalRow, setDirectModalRow] = useState(null);
    const [viewDetail, setViewDetail] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [directModalLoading, setDirectModalLoading] = useState(false);

    // ── Step 1 form fields ──────────────────────────────────────────────────────
    const [requestId, setRequestId] = useState(null);
    const [targetResource, setTargetResource] = useState(null);
    const [requestedHours, setRequestedHours] = useState("");
    const [periodYear, setPeriodYear] = useState(currentYear);
    const [periodMonth, setPeriodMonth] = useState(currentMonth);
    const [notes, setNotes] = useState(null);
    const [status, setStatus] = useState(0);
    const [attachments, setAttachments] = useState([]);
    const [changeRequest, setChangeRequest] = useState(null);

    // Change Request search
    const [changeRequests, setChangeRequests] = useState([]);


    const [isValidated, setIsValidated] = useState(false);


    // ── Step 2 state ────────────────────────────────────────────────────────────
    const [options, setOptions] = useState(null);
    const [resourcesPool, setResourcesPool] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [selectedOptionIdx, setSelectedOptionIdx] = useState(null);
    const [editableItems, setEditableItems] = useState([]);
    const [validation, setValidation] = useState(null);
    const [validationLoading, setValidationLoading] = useState(false);

    // ── Review (approve/reject) ─────────────────────────────────────────────────
    const [reviewNotes, setReviewNotes] = useState("");
    const [reviewNotesError, setReviewNotesError] = useState("");

    // ── Form errors ─────────────────────────────────────────────────────────────
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        fetchData();
    }, [pageNum, perPageItems]);

    const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch = false) => {
        if (!projectID) {
            setData([]);
            return;
        }
        const payload = {
            projectId: projectID,
            page: pageNumber ?? pageNum,
            size: pageSize ?? perPageItems,
            search: forceClearSearch ? null : (searchTerm ?? tableSearch)
        };


        dispatch(getReshuffleRequests(payload));
    };

    useEffect(() => {
        if (allReshuffleRequests && allReshuffleRequests.length > 0) {
            if (pageNum === 1) {
                setData(
                    allReshuffleRequests
                        .filter((item) => item.id !== deleteId)
                        .sort((a, b) => {
                            return b.isFlagged - a.isFlagged;
                        })
                );
            }
            else {
                setData((prevData) => {
                    const newMap = new Map(
                        allReshuffleRequests.map((item) => [item.id, item])
                    );

                    const updatedPrevData = prevData.map((item) =>
                        newMap.has(item.id) ? newMap.get(item.id) : item
                    );

                    const prevDataIds = new Set(prevData.map((item) => item.id));
                    const newItems = allReshuffleRequests.filter(
                        (shift) => !prevDataIds.has(shift.id)
                    );

                    return [...updatedPrevData, ...newItems]
                        .filter((item) => item.id !== deleteId)
                        .sort((a, b) => b.isFlagged - a.isFlagged);
                });
            }
        } else if (allReshuffleRequests && allReshuffleRequests.length === 0) {
            setData([]);
        }
    }, [allReshuffleRequests]);

    // ── Change request search (debounced) ───────────────────────────────────────
    const changRequestSearchdebounced = useRef(
        debounce((value, params) => searchChangRequestHandler(value, params), 500)
    );

    const changRequestSearch = useCallback((value, params) => {
        changRequestSearchdebounced.current(value, params);
    }, []);

    const searchChangRequestHandler = (value, params) => {
        if (!params.projectId) {
            setChangeRequests([]);
            return;
        }
        searchService
            .ChangeRequests(value, params)
            .then((res) => {
                const transformedArray = res.map((obj) => ({
                    label: obj.name,
                    value: obj.id,
                }));
                setChangeRequests(transformedArray);
            })
            .catch(() => {
                setChangeRequests([]);
            });
    };

    // ── Resource pool helpers ───────────────────────────────────────────────────
    const getResourceDataFromPool = (donorResourceId) => {
        const sel = (resourcesPool).find((r) => r.resource?.id === donorResourceId);
        return sel ? sel : null;
    }

    // ── Validation row helpers ──────────────────────────────────────────────────
    const getRowErrors = (donorResourceId) =>
        (validation?.errors || []).filter((e) => e.resourceId === donorResourceId);
    const getRowWarnings = (donorResourceId) =>
        (validation?.warnings || []).filter((w) => w.resourceId === donorResourceId);
    const getGlobalErrors = () =>
        (validation?.errors || []).filter((e) => e.isGlobal);

    // ── Build create DTO ────────────────────────────────────────────────────────
    const buildDto = () => {
        const items = editableItems.map((item) => ({
            donorResourceId: item.donorResource?.id || null,
            hoursRequested: parseFloat(item.targetHoursAdded) || 0,
        }));

        return {
            resourceId: targetResource?.id,
            year: periodYear,
            month: periodMonth,
            notes,
            changeRequestId: changeRequest?.value || null,
            items: items
        };
    };

    function rankReshuffleOption(option) {
        const cost = option.totalCostDelta;
        const bill = option.totalBillDelta;

        // Cost Neutral + Bill Neutral
        if (cost === 0 && bill === 0) {
            return {
                ...option,
                priority: 1,
                priorityLabel: "Ideal",
                summary:
                    "Cost-neutral and bill-neutral reshuffle. No impact on project financials."
            };
        }

        // Improves economics
        if (cost <= 0 && bill >= 0) {
            return {
                ...option,
                priority: 2,
                priorityLabel: "Beneficial",
                summary:
                    "Improves project economics through reduced cost and/or increased bill value."
            };
        }

        // Trade-off
        if (
            (cost < 0 && bill < 0) ||
            (cost > 0 && bill > 0)
        ) {
            return {
                ...option,
                priority: 3,
                priorityLabel: "Trade-off",
                summary:
                    "Financial trade-off requiring review. Both cost and bill move in the same direction."
            };
        }

        // Negative
        return {
            ...option,
            priority: 4,
            priorityLabel: "Negative",
            summary:
                "Increases project cost and/or reduces billable value."
        };
    }

    function orderReshuffleOptions(options) {
        return options
            .map(rankReshuffleOption)
            .sort((a, b) => {
                // Priority bucket first
                if (a.priority !== b.priority) {
                    return a.priority - b.priority;
                }

                // Within bucket:
                // lower cost delta is better
                // higher bill delta is better
                const scoreA =
                    (-a.totalCostDelta) + a.totalBillDelta;

                const scoreB =
                    (-b.totalCostDelta) + b.totalBillDelta;

                return scoreB - scoreA;
            });
    }


    // ── Fetch options (Step 1 → 2) ──────────────────────────────────────────────
    const handleGetOptions = async () => {
        const res = targetResource;
        const hrs = requestedHours;

        if (!res) {
            setFormErrors((e) => ({ ...e, targetResource: "Target resource is required" }));
            return;
        }
        else {
            setFormErrors((e) => ({ ...e, targetResource: null }));
        }

        if (!hrs || parseFloat(hrs) <= 0) {
            setFormErrors((e) => ({ ...e, requestedHours: "Hours must be greater than 0" }));
            return;
        }
        else {
            setFormErrors((e) => ({ ...e, requestedHours: null }));
        }

        setFormErrors((e) => ({ ...e, period: null }));

        setFormErrors({});
        setOptionsLoading(true);
        try {
            let data = await reshuffleservice.getReshuffleOptions(projectID, {
                resourceId: res.id,
                requestedHours: parseFloat(hrs),
                year: periodYear,
                month: periodMonth,
            });

            let orderSystemOptions = orderReshuffleOptions(data?.systemOptions || []);
            data.systemOptions = orderSystemOptions;
            setOptions(data);
            setValidation(null);
            setEditableItems([]);
            setSelectedOptionIdx(null);
            if (modalMode === "create")
                setWizardStep(2);
        } catch {
            dispatch(generalActions.showToast({ title: "Failed to load reshuffle options.", type: "error" }));
        } finally {
            setOptionsLoading(false);
        }
    };

    // ── Select system option ────────────────────────────────────────────────────
    const handleSelectOption = (opt) => {
        setIsValidated(false);
        setSelectedOptionIdx(opt.optionIndex);
        setEditableItems(
            opt.items.map((item, i) => ({
                tempId: `opt-${i}`,
                year: item.year,
                month: item.month,
                donorResource: item.donorResource,
                donorHoursReduced: item.donorHoursReduced,
                targetHoursAdded: item.targetHoursAdded,
            }))
        );
        setValidation(null);
    };

    // ── Validate (Step 2 → 3) ───────────────────────────────────────────────────
    const handleValidate = async () => {
        if (editableItems.length === 0) {
            const result = { errors: [{ isValid: false, isGlobal: true, message: "At least one donor item is required" }] };
            setValidation(result);
            return;
        }
        //if any item has targetHoursAdded <= 0, show error
        if (editableItems.some((item) => !item.targetHoursAdded || parseFloat(item.targetHoursAdded) <= 0)) {
            const result = { errors: [{ isValid: false, isGlobal: true, message: "All target hours must be greater than 0" }] };
            setValidation(result);
            return;
        }

        setValidationLoading(true);
        try {
            const result = await reshuffleservice.validateReshuffleRequest(projectID, buildDto());
            setValidation(result);
            if (!isValidated && (result.warnings && result.warnings.length > 0 || result.errors && result.errors.length > 0)) {
                setIsValidated(true);
                return;
            }
            setIsValidated(true);
            if (result.isValid)
                setWizardStep(3);
        } catch {
            dispatch(generalActions.showToast({ title: "Validation failed. Please try again.", type: "error" }));
        } finally {
            setValidationLoading(false);
        }
    };

    // ── Confirm & Create (Step 3) ───────────────────────────────────────────────
    const handleConfirmCreate = async () => {
        setValidationLoading(true);
        try {
            const dto = buildDto();
            const result = await reshuffleservice.validateReshuffleRequest(projectID, dto);
            if (!result.isValid) {
                setValidation(result);
                setWizardStep(2);
                setValidationLoading(false);
                return;
            }
            createReshuffleRequest({ projectId: projectID, payload: dto, size: perPageItems });
            // modalLoading will toggle → Table.jsx useEffect closes modal automatically
        } catch {
            dispatch(generalActions.showToast({ title: "Something went wrong. Please try again.", type: "error" }));
            setValidationLoading(false);
        }
    };

    // ── Save handler (called by Table.jsx modal save button) ────────────────────
    const handleSave = async () => {
        if (modalMode === "edit") {

            if (editableItems.length === 0) {
                const result = { errors: [{ isValid: false, isGlobal: true, message: "At least one donor item is required" }] };
                setValidation(result);
                return;
            }

            setValidationLoading(true);
            try {
                const result = await reshuffleservice.validateReshuffleRequest(projectID, buildDto());
                setValidation(result);

                if (!result.isValid)
                    return;

                if (!isValidated && (result.warnings && result.warnings.length > 0 || result.errors && result.errors.length > 0)) {
                    setIsValidated(true);
                    return;
                }
                setIsValidated(true);
            } catch {
                dispatch(generalActions.showToast({ title: "Validation failed. Please try again.", type: "error" }));
            } finally {
                setValidationLoading(false);
            }

            const items = editableItems.map((item) => ({
                year: item.year,
                month: item.month,
                donorResourceId: item.donorResource?.id || null,
                hoursRequested: parseFloat(item.targetHoursAdded) || 0,
            }));

            updateReshuffleRequest({
                projectId: projectID,
                id: requestId,
                payload: {
                    notes,
                    changeRequestId: changeRequest?.value || null,
                    items: items,
                },
                size: perPageItems,
            });
        } else if (wizardStep === 1) {
            fetchResourcePool();
            handleGetOptions();
        } else if (wizardStep === 2) {
            handleValidate();
        } else if (wizardStep === 3) {
            handleConfirmCreate();
        }
    };

    //delete handler
    const handleDelete = (selectedRow) => {
        const id = selectedRow?.id;
        deleteReshuffleRequest({ projectId: projectID, id: selectedRow?.id, size: perPageItems })
        setDeletedId(id);
    };
    // ── Row select callback (intercepted from Table.jsx handleEditAction) ────────
    const handleCreate = (val) => {
        resetForm();
        if (val)
            setModalMode("create");
    };

    useEffect(() => {
        if (selectedRowId && modalMode === "edit") {
            handleEditSelect();
        }
    }, [selectedRowId]);

    const handleRowSelect = async (row) => {
        if (!row || !row.id) {
            return;
        }
        else
            setSelectedRowId(row?.id);
    }

    const handleEditSelect = () => {
        resetForm();
        if (!selectedRowId) {
            return;
        }

        // Edit mode
        setModalMode("edit");
        setDirectModalLoading(true);
        try {
            fetchResourcePool();
            reshuffleservice.getSingleReshuffleRequest(projectID, selectedRowId).then((data) => {
                setDirectModalLoading(false);
                setRequestId(data.id);
                setOptions(null);
                setIsValidated(false);
                setResourcesPool([]);
                setValidation(null);
                setTargetResource(data.targetResource);
                setRequestedHours(data.requestedHours);
                setPeriodYear(data.year);
                setPeriodMonth(data.month);
                setStatus(data.status);
                setNotes(data.notes);
                setChangeRequest(data.changeRequest);
                setAttachments(data.attachments || []);
                if (data.changeRequest) {
                    setChangeRequest({
                        value: data.changeRequest.id,
                        label: data.changeRequest.name,
                    });
                }
                if (data.items && data.items.length > 0) {
                    setEditableItems(
                        data.items.map((item, i) => ({
                            tempId: item.id,
                            year: item.year,
                            month: item.month,
                            donorResource: item.donorResource,
                            donorHoursReduced: item.donorHoursReduced,
                            donorForecastHourlyCostRate: item.donorForecastHourlyCostRate,
                            donorForecastHourlyBillRate: item.donorForecastHourlyBillRate,
                            targetHoursAdded: item.targetHoursAdded,
                            targetForecastHourlyCostRate: item.targetForecastHourlyCostRate,
                            targetForecastHourlyBillRate: item.targetForecastHourlyBillRate,
                            costDelta: item.costDelta,
                            billDelta: item.billDelta,
                        }))
                    );
                }
            });

        } catch {
            dispatch(generalActions.showToast({ title: "Failed to load details.", type: "error" }));
            setShowDirectModal(false);
            setDirectModalLoading(false);
        }
    };

    const resetForm = () => {
        setModalMode("");
        setWizardStep(1);
        setSelectedRowId(null);
        setRequestId(null);
        setTargetResource(null);
        setRequestedHours("");
        setPeriodYear(currentYear);
        setPeriodMonth(currentMonth);
        setNotes(null);
        setChangeRequest(null);
        setChangeRequests([]);
        setOptions(null);
        setIsValidated(false);
        setResourcesPool([]);
        setEditableItems([]);
        setValidation(null);
        setSelectedOptionIdx(null);
        setFormErrors({});
        setValidationLoading(false);
        setOptionsLoading(false);
        setStatus(0);
        setAttachments([]);
    };

    // ── Editable item helpers ───────────────────────────────────────────────────
    const addEditableItem = () => {
        setValidation(null);
        setIsValidated(false);
        setEditableItems((items) => [
            ...items,
            {
                tempId: `new-${Date.now()}`,
                year: periodYear,
                month: periodMonth,
                donorResource: null,
                donorHoursReduced: "",
                targetHoursAdded: "",
            },
        ]);
    };

    const removeEditableItem = (tempId) => {
        setIsValidated(false);
        setEditableItems((items) => items.filter((i) => i.tempId !== tempId));
    };

    const updateEditableItem = (tempId, field, value) => {
        setIsValidated(false);
        setEditableItems((items) =>
            items.map((i) => (i.tempId === tempId ? { ...i, [field]: value } : i))
        );
    };

    // ── Direct modal actions (view / summary / approve / reject / submit / execute / cancel) ──
    const handleAction = (action, row) => {
        setDirectModalRow(row);
        switch (action) {
            case "view":
                setDirectModalMode("view");
                setShowDirectModal(true);
                fetchViewDetail(row.id);
                break;
            case "summary":
                setDirectModalMode("summary");
                setShowDirectModal(true);
                fetchSummary(row.id);
                break;
            case "approve":
                setDirectModalMode("approve");
                setReviewNotes("");
                setReviewNotesError("");
                setShowDirectModal(true);
                break;
            case "reject":
                setDirectModalMode("reject");
                setReviewNotes("");
                setReviewNotesError("");
                setShowDirectModal(true);
                break;
            case "submit":
                submitReshuffleRequest({ projectId: projectID, id: row.id, size: perPageItems });
                break;
            case "execute":
                executeReshuffleRequest({ projectId: projectID, id: row.id, size: perPageItems });
                break;
            case "cancel":
                cancelReshuffleRequest({ projectId: projectID, id: row.id, size: perPageItems });
                break;
            default:
                break;
        }
    };

    const fetchViewDetail = async (id) => {
        setDirectModalLoading(true);
        try {
            const data = await reshuffleservice.getSingleReshuffleRequest(projectID, id);
            setViewDetail(data);
        } catch {
            dispatch(generalActions.showToast({ title: "Failed to load details.", type: "error" }));
            setShowDirectModal(false);
        } finally {
            setDirectModalLoading(false);
        }
    };

    const fetchSummary = async (id) => {
        setDirectModalLoading(true);
        try {
            const data = await reshuffleservice.getReshuffleSummary(projectID, id);
            setSummaryData(data);
        } catch {
            dispatch(generalActions.showToast({ title: "Failed to load summary.", type: "error" }));
            setShowDirectModal(false);
        } finally {
            setDirectModalLoading(false);
        }
    };

    const closeDirectModal = () => {
        setShowDirectModal(false);
        setDirectModalRow(null);
        setViewDetail(null);
        setSummaryData(null);
        setReviewNotes("");
        setReviewNotesError("");
    };

    const handleApproveConfirm = () => {
        approveReshuffleRequest({ projectId: projectID, id: directModalRow.id, reviewNotes, size: perPageItems });
        closeDirectModal();
    };

    const handleRejectConfirm = () => {
        if (!reviewNotes.trim()) {
            setReviewNotesError("Review notes are required for rejection.");
            return;
        }
        rejectReshuffleRequest({ projectId: projectID, id: directModalRow.id, reviewNotes, size: perPageItems });
        closeDirectModal();
    };

    // ── Modal title / save label ────────────────────────────────────────────────
    const getSaveBtnLabel = () => {
        if (modalMode === "edit") return pageLoading ? (isValidated ? "Saving…" : "Validating…") : (isValidated ? "Save" : "Validate");
        if (wizardStep === 1) return optionsLoading ? "Loading…" : "Get Options";
        if (wizardStep === 2) return validationLoading ? "Validating…" : (isValidated ? "Next" : "Validate");
        return validationLoading ? "Creating…" : "Create";
    };

    const isSaveBtnDisabled = () => {
        if (modalMode === "edit" && pageLoading) return true;
        if (wizardStep === 1 && optionsLoading) return true;
        if ((wizardStep === 2 || wizardStep === 3) && validationLoading) return true;
        return false;
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // FORM BODY (rendered inside Table.jsx's Modal)
    // ─────────────────────────────────────────────────────────────────────────────
    const renderUserHoverCell = (user, size = 24) => {
        if (!user) return null;
        return (
            <HoverCard
                HoverCardTrigger={
                    <div className={styles.userCell}>
                        <Avatar
                            name={user.name}
                            image={{ src: user.image }}
                            color="colorful"
                            size={20} />
                        <span>{user.name}</span>
                    </div>
                }
                AvatarId={user.id}
                AvatarName={user.name}
                AvatarImage={user.image}
                AvatarDesignation={user.title}
                AvatarEmail={user.email}
                AvatarPhone={user.phoneNumber}
                AvatarIsExternal={user.isExternal}
            />
        );
    };

    const renderDeltaPill = (delta, isBill, forceBackground = false) => {
        const threshold = 0.01;

        let className = styles.neutralBadge;
        let text = `${isBill ? "Bill" : "Cost"} `;
        let icon = <></>;

        if (delta >= threshold) {
            className = styles.increaseBadge;
            icon = <TrendingUp size={12} className="mr-1" />;
            text += `Increase • ${`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Math.abs(delta))}`}`;
        } else if (delta <= -threshold) {
            className = styles.decreaseBadge;
            icon = <TrendingDown size={12} className="mr-1" />;
            text += `Decrease • ${`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Math.abs(delta))}`}`;
        }
        else {
            text += "Neutral";
        }

        return <span className={`${className} ${forceBackground ? styles.summaryDeltaBadge : ""}`}>{icon}{text}</span>;
    };

    // ── Step 1 ──────────────────────────────────────────────────────────────────
    const renderStep1 = () => (
        <div className={styles.wizardStep}>
            <div className={styles.stepIndicator}>
                <div className={`${styles.stepDot} ${styles.active}`}>1</div>
                <div className={styles.stepLine} />
                <div className={styles.stepDot}>2</div>
                <div className={styles.stepLine} />
                <div className={styles.stepDot}>3</div>
            </div>


            <div className={styles.formRow4}>
                {/* Target Resource */}
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        Target Resource <span className={styles.required}>*</span>
                    </label>
                    <UserSelect
                        multiple={false}
                        refType={UserSearchRefType.Project}
                        refId={projectID}
                        value={targetResource}
                        onChange={setTargetResource}
                        label=""
                        buttonLabel="Select Resource"
                        selectedUsersDisplayMode="full"
                        isRequired={true}
                        className={"mt-1"}
                    />
                    {formErrors.targetResource && <p className={styles.errorText}>{formErrors.targetResource}</p>}
                </div>
                {/* Requested Hours */}
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        Hours needed <span className={styles.required}>*</span>
                    </label>
                    <Input
                        inputwd="flex-1"
                        value={requestedHours}
                        type="number"
                        handleChange={setRequestedHours}
                        placeholder="e.g. 20"
                    />
                    {formErrors.requestedHours && <p className={styles.errorText}>{formErrors.requestedHours}</p>}
                </div>
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Period</label>
                    <div className={styles.periodPicker}>
                        <DropDown
                            options={MONTH_ABBR.map((m, i) => ({ name: m, value: i + 1 }))}
                            defaultVal={MONTH_ABBR[periodMonth - 1]}
                            onChange={(e) => setPeriodMonth(+e)}
                            dropDownwd={"flex-1"}
                        />
                        <DropDown
                            options={YEAR_OPTIONS.map((y, i) => ({ name: y, value: y }))}
                            defaultVal={periodYear}
                            onChange={(e) => setPeriodYear(+e)}
                            dropDownwd={"flex-1"}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.formRow3}>
                {/* Change Request */}
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Change Request</label>
                    {changeRequest ? (
                        <div className={styles.selectedTag}>
                            <span>{changeRequest.label}</span>
                            <button onClick={() => { setChangeRequest(null); }}>×</button>
                        </div>
                    ) :
                        <>

                            <Searchable
                                searchText={(e) =>
                                    changRequestSearch(e, { projectId: projectID })
                                }
                                SelectText="change requests"
                                options={changeRequests}
                                defaultVal={changeRequest}
                                onChange={setChangeRequest}
                            />
                        </>

                    }
                </div>
            </div>

            {/* Notes */}
            <div className={`${styles.formGroup} mt-3`}>
                <label className={styles.formLabel}>Notes</label>
                <RichTextEditor
                    content={notes}
                    onChange={(value) => {
                        setNotes(value)
                    }}
                />
            </div>

            {/* Attachments */}
            <div className={`${styles.formGroup} mt-3`}>
                <EntityFileUpload
                    entityId={requestId}
                    entityType="ReshuffleRequest"
                    attachments={attachments}
                    onAttachmentsChange={() => {
                        if (requestId) {
                            reshuffleservice
                                .getSingleReshuffleRequest(projectID, requestId)
                                .then((reshuffleRequestData) => {
                                    setAttachments(reshuffleRequestData.attachments);
                                })
                                .catch((error) => {
                                    console.error(
                                        "Error fetching request data:",
                                        error
                                    );
                                });
                        }
                    }}
                    disabled={!requestId}
                />
            </div>
        </div>
    );

    // ── Step 2 ──────────────────────────────────────────────────────────────────
    const renderStep2 = () => {
        const hasSystemOptions = (options?.systemOptions || []).length > 0;
        const hasPool = (options?.resourcePool || []).length > 0;
        const globalErrors = getGlobalErrors();
        const targetResource = options?.targetResource;
        const targetHourlyCostRate = options?.targetHourlyCostRate || 0;
        const targetHourlyBillRate = options?.targetHourlyBillRate || 0;

        return (
            <div className={styles.step2Container}>
                <button className={styles.backBtn} onClick={() => setWizardStep(1)}>
                    <div className="d-flex align-items-center gap-1">
                        <ArrowLeft size={16} />
                        Back to Request Info
                    </div>
                </button>
                <div className={styles.stepIndicator}>
                    <div className={`${styles.stepDot} ${styles.done}`}><span className={styles.stepCheck}><Check size={16} /></span></div>
                    <div className={styles.stepLine} />
                    <div className={`${styles.stepDot} ${styles.active}`}>2</div>
                    <div className={styles.stepLine} />
                    <div className={styles.stepDot}>3</div>
                </div>

                <div className={styles.step2Layout}>
                    {/* Left panel – System options */}
                    <div className={styles.optionsPanel}>
                        <div className={styles.panelTitle}>System Options</div>
                        {!hasSystemOptions && !hasPool && (
                            <div className={styles.infoBanner}>
                                No available donors found for the selected period. All project resources are either fully allocated or have no forecast hours.
                            </div>
                        )}
                        {!hasSystemOptions && hasPool && (
                            <div className={styles.infoBannerInfo}>
                                No automatic options available. You can manually configure the request.
                            </div>
                        )}
                        {hasSystemOptions && (options.systemOptions || []).map((opt) => (
                            <div
                                key={opt.optionIndex}
                                className={`${styles.optionCard} ${selectedOptionIdx === opt.optionIndex ? styles.optionCardSelected : ""}`}
                                onClick={() => handleSelectOption(opt)}
                            >
                                <div className={styles.optionCardHeader}>
                                    {renderDeltaPill(opt.totalCostDelta, false)}
                                    {renderDeltaPill(opt.totalBillDelta, true)}
                                    <span className={styles.optionHoursBadge}>{opt.totalTargetHoursGained} hrs gained</span>
                                </div>
                                <table className={styles.optionBreakdownTable}>
                                    <thead>
                                        <tr>
                                            <th>Role</th>
                                            <th>Resource</th>
                                            <th>Hrs</th>
                                            <th>Cost Rate</th>
                                            <th>Bill Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(opt.items || []).map((item, i) => (
                                            <tr key={i}>
                                                <td><span className={styles.roleBadgeDonor}>Donor</span></td>
                                                <td>{renderUserHoverCell(item.donorResource, 18)}</td>
                                                <td>{`-${displayDecimalFormatted(setting, Number(item.donorHoursReduced))}`}</td>
                                                <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(item.donorHourlyCostRate))}`}</td>
                                                <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(item.donorHourlyBillRate))}`}</td>
                                            </tr>
                                        ))}
                                        <tr key={`${opt.optionIndex}-target`}>
                                            <td><span className={styles.roleBadgeTarget}>Target</span></td>
                                            <td>{renderUserHoverCell(targetResource, 18)}</td>
                                            <td>{`+${displayDecimalFormatted(setting, Number(opt.totalTargetHoursGained))}`}</td>
                                            <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(targetHourlyCostRate))}`}</td>
                                            <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(targetHourlyBillRate))}`}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <button className={styles.selectOptionBtn} onClick={() => handleSelectOption(opt)}>
                                    Select this option
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Right panel – Editable grid */}
                    <div className={styles.editPanel}>
                        <div className={styles.panelTitle}>Configure Request</div>

                        {globalErrors.length > 0 && (
                            <div className={styles.errorBanner}>
                                {globalErrors.map((e, i) => <p key={i}>{e.message}</p>)}
                            </div>
                        )}

                        {validation && Math.abs(validation.preview?.totalCostDelta) >= 0.01 && (
                            <div className={styles.costWarningBanner}>
                                This reshuffle will change the project forecasted cost by{" "}
                                {getAccountCurrency(setting)} {displayDecimalFormatted(setting, Number(validation.preview.totalCostDelta))}
                            </div>
                        )}
                        {validation && Math.abs(validation.preview?.totalBillDelta) >= 0.01 && (
                            <div className={styles.costWarningBanner}>
                                This reshuffle will change the project forecasted bill by{" "}
                                {getAccountCurrency(setting)} {displayDecimalFormatted(setting, Number(validation.preview.totalBillDelta))}
                            </div>
                        )}

                        {editableItems.length === 0 ? (
                            <div className={styles.emptyGrid}>Select a system option or add items manually.</div>
                        ) : (
                            <div className={styles.editableGrid}>
                                <div className={styles.gridHeader}>
                                    <span>Donor</span>
                                    <span>YTD Hours Buffer</span>
                                    <span>
                                        <Tooltip content={"Available hours to fund this transfer"} relationship="label" positioning="above-start" withArrow>
                                            <span>{`Available Hrs`}</span>
                                        </Tooltip>
                                    </span>
                                    <span>
                                        <Tooltip content={"Hours to be deducted from the donor resource to fund this transfer"} relationship="label" positioning="above-start" withArrow>
                                            <span>{`Requested Hrs`}</span>
                                        </Tooltip>
                                    </span>
                                    <span></span>
                                </div>
                                {editableItems.map((item) => {
                                    const rowErrors = getRowErrors(item.donorResource?.id);
                                    const rowWarnings = getRowWarnings(item.donorResource?.id);
                                    const resourceData = getResourceDataFromPool(item.donorResource?.id);
                                    return (
                                        <div key={item.tempId} className={styles.gridRow}>
                                            <div className={styles.gridCell}>
                                                {/* {renderUserHoverCell(item.donorResource, 18)} */}
                                                {<div className={styles.donorSelectWrapper}>
                                                    <DropDown
                                                        options={resourcesPool.map((r) => (
                                                            {
                                                                name: `${r.resource?.name} (${displayDecimalFormatted(setting, Number(r.availableFormattedHours || 0))} hrs)`,
                                                                value: r.resource?.id,
                                                            }
                                                        ))}
                                                        defaultVal={resourceData?.resource ? renderUserHoverCell(resourceData?.resource, 18) : ""}
                                                        onChange={(e) => {
                                                            const sel = e ? resourcesPool.find((r) => r.resource?.id === e) : null;
                                                            updateEditableItem(item.tempId, "donorResource", sel?.resource || null);
                                                        }}
                                                        placeholder="Select Donor"
                                                        dropDownwd={"flex-1"}
                                                    />

                                                </div>}
                                            </div>
                                            <div className={styles.gridCell}>
                                                <p
                                                    className={`
                                                            text-center py-[9px] rounded-md me-2 Input_wrapper position-relative`}
                                                    style={{
                                                        backgroundColor: Number(resourceData?.availableFormattedHours || 0) >= Number(item.donorHoursReduced || 0) ? "#107c101f" : "#dc26261f",
                                                        color: Number(resourceData?.availableFormattedHours || 0) >= Number(item.donorHoursReduced || 0) ? "#107c10" : "#dc2626",
                                                    }}
                                                >
                                                    {`${displayDecimalFormatted(
                                                        setting,
                                                        Number(resourceData?.availableHours || 0)
                                                    )}`}
                                                </p>
                                            </div>
                                            <div className={styles.gridCell}>
                                                <Tooltip content={"Available hours to fund this transfer"} relationship="label" positioning="above-start" withArrow>
                                                    <p
                                                        className={`
                                                            text-center py-[9px] rounded-md me-2 Input_wrapper position-relative`}
                                                        style={{
                                                            backgroundColor: Number(resourceData?.availableFormattedHours || 0) >= Number(item.targetHoursAdded || 0) ? "#107c101f" : "#dc26261f",
                                                            color: Number(resourceData?.availableFormattedHours || 0) >= Number(item.targetHoursAdded || 0) ? "#107c10" : "#dc2626",
                                                        }}
                                                    >
                                                        {`${displayDecimalFormatted(
                                                            setting,
                                                            Number(resourceData?.availableFormattedHours || 0)
                                                        )}`}
                                                    </p>
                                                </Tooltip>

                                            </div>
                                            <div className={styles.gridCell}>
                                                <Input
                                                    inputwd="flex-1"
                                                    value={item.targetHoursAdded}
                                                    min={0.1}
                                                    type="number"
                                                    handleChange={(value) => updateEditableItem(item.tempId, "targetHoursAdded", value)}
                                                    placeholder=""
                                                />

                                            </div>
                                            <div className={`${styles.gridCell}`}>
                                                <X className="cursor-pointer" color="#dc2626" size={16} onClick={() => removeEditableItem(item.tempId)} />
                                            </div>
                                            {rowErrors.length > 0 && rowErrors.map((error, index) => (

                                                <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.errorBanner}`}>
                                                    {error.message}{error.suggestedHours != null ? ` Max: ${error.suggestedHours} hrs` : ""}
                                                </div>
                                            ))}
                                            {Number(resourceData?.availableFormattedHours || 0) < Number(item.targetHoursAdded || 0) &&

                                                <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.infoBanner} mt-1`}>
                                                    {`Available hours for this resource is ${displayDecimalFormatted(
                                                        setting,
                                                        Number(resourceData?.availableFormattedHours || 0)
                                                    )} hrs. Consider reducing donor hours or selecting a different donor.`}
                                                </div>
                                            }
                                            {rowWarnings.length > 0 && rowWarnings.map((warning, index) => (

                                                <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.infoBanner} mt-1`}>
                                                    {warning.message}{warning.suggestedHours != null ? ` Max: ${warning.suggestedHours} hrs` : ""}
                                                </div>
                                            ))}

                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <button className={styles.addRowBtn} onClick={addEditableItem}>+ Add Row</button>
                    </div>
                </div>
            </div>
        );
    };

    // ── Step 3 ──────────────────────────────────────────────────────────────────
    const renderStep3 = () => (
        <div className={styles.wizardStep}>
            <button className={styles.backBtn} onClick={() => setWizardStep(2)}>
                <div className="d-flex align-items-center gap-1">
                    <ArrowLeft size={16} />
                    Back to Configuration
                </div>
            </button>
            <div className={styles.stepIndicator}>
                <div className={`${styles.stepDot} ${styles.done}`}><span className={styles.stepCheck}><Check size={16} /></span></div>
                <div className={styles.stepLine} />
                <div className={`${styles.stepDot} ${styles.done}`}><span className={styles.stepCheck}><Check size={16} /></span></div>
                <div className={styles.stepLine} />
                <div className={`${styles.stepDot} ${styles.active}`}>3</div>
            </div>

            {validation?.preview && <ReshuffleSummaryPreview preview={validation.preview} />}
        </div>
    );

    // ── Edit mode ────────────────────────────────────────────────────────────────
    const renderEditMode = () => {
        const showGetOptionsBtn = editableItems.length === 0;
        const globalErrors = getGlobalErrors();
        const selectedOption = options?.systemOptions?.find(o => o.optionIndex === selectedOptionIdx);
        const totalCostDelta = validation?.preview?.totalCostDelta ??
            (selectedOption ?
                selectedOption.totalCostDelta :
                ((editableItems || []).length > 0 ? (editableItems || []).reduce((acc, item) => acc + item.costDelta, 0) : null));
        const totalBillDelta = validation?.preview?.totalBillDelta ??
            (selectedOption ?
                selectedOption.totalBillDelta :
                ((editableItems || []).length > 0 ? (editableItems || []).reduce((acc, item) => acc + item.billDelta, 0) : null));

        return (
            <div className={styles.editModeContainer}>
                {/* Read-only header */}
                {
                    directModalLoading ?
                        <Skeleton height={60} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                        :
                        <>
                            <div className={styles.summaryHeader}>
                                <div className={styles.summaryCell}>
                                    <p className={styles.summaryTitle}>
                                        {renderUserHoverCell(targetResource)}
                                    </p>
                                </div>
                                <div className={styles.summaryCell}>
                                    <p className={styles.summarySubtitle}>
                                        <span className={styles.summaryStatusBadge}>
                                            {`${formatMonthYear(periodYear, periodMonth)} • ${displayDecimalFormatted(setting, Number(requestedHours))} hrs`}
                                        </span>
                                    </p>
                                </div>
                                {
                                    totalCostDelta != null && (
                                        <div className={styles.summaryCell}>
                                            {renderDeltaPill(totalCostDelta, false, true)}
                                        </div>
                                    )
                                }
                                {
                                    totalBillDelta != null && (
                                        <div className={styles.summaryCell}>
                                            {renderDeltaPill(totalBillDelta, true, true)}
                                        </div>
                                    )
                                }
                                <div className={styles.summaryCell}>
                                    {status !== undefined && (
                                        <span className={styles.summaryStatusBadge}>
                                            {getReshuffleStatusLabel(status)}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className={styles.formRow3}>
                                {/* Change Request */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Change Request</label>
                                    {changeRequest ? (
                                        <div className={styles.selectedTag}>
                                            <span>{changeRequest.label}</span>
                                            <button onClick={() => { setChangeRequest(null); }}>×</button>
                                        </div>
                                    ) :
                                        <Searchable
                                            searchText={(e) =>
                                                changRequestSearch(e, { projectId: projectID })
                                            }
                                            SelectText="change requests"
                                            options={changeRequests}
                                            defaultVal={changeRequest}
                                            onChange={setChangeRequest}
                                            loading={directModalLoading}
                                        />
                                    }
                                </div>
                            </div>
                        </>

                }

                {/* Get Options section (only when items cleared) */}
                {showGetOptionsBtn && !directModalLoading && (
                    <div className={styles.getOptionsSection}>
                        {
                            !options && (
                                <>
                                    <p className={styles.getOptionsHint}>All items cleared. Fetch new options to repopulate.</p>
                                    <button
                                        className={styles.getOptionsBtn}
                                        disabled={optionsLoading}
                                        onClick={() => targetResource && handleGetOptions()}
                                    >
                                        {optionsLoading ? <Spinner size="sm" animation="border" /> : "Get Options"}
                                    </button>
                                </>
                            )
                        }

                        {options && (options.systemOptions || []).length > 0 && (

                            <div className={styles.optionsList}>
                                {(options.systemOptions || []).map((opt) => (
                                    <div
                                        key={opt.optionIndex}
                                        className={`${styles.optionCard} ${selectedOptionIdx === opt.optionIndex ? styles.optionCardSelected : ""}`}
                                        onClick={() => handleSelectOption(opt)}
                                    >
                                        <div className={styles.optionCardHeader}>
                                            {renderDeltaPill(opt.totalCostDelta, false)}
                                            {renderDeltaPill(opt.totalBillDelta, true)}
                                            <span className={styles.optionHoursBadge}>{opt.totalTargetHoursGained} hrs gained</span>
                                        </div>
                                        <table className={styles.optionBreakdownTable}>
                                            <thead>
                                                <tr>
                                                    <th>Role</th>
                                                    <th>Resource</th>
                                                    <th>Hrs</th>
                                                    <th>Cost Rate</th>
                                                    <th>Bill Rate</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(opt.items || []).map((item, i) => (
                                                    <tr key={i}>
                                                        <td><span className={styles.roleBadgeDonor}>Donor</span></td>
                                                        <td>{renderUserHoverCell(item.donorResource, 18)}</td>
                                                        <td>{`-${displayDecimalFormatted(setting, Number(item.donorHoursReduced))}`}</td>
                                                        <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(item.donorHourlyCostRate))}`}</td>
                                                        <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(item.donorHourlyBillRate))}`}</td>
                                                    </tr>
                                                ))}
                                                <tr key={`${opt.optionIndex}-target`}>
                                                    <td><span className={styles.roleBadgeTarget}>Target</span></td>
                                                    <td>{renderUserHoverCell(options?.targetResource, 18)}</td>
                                                    <td>{`+${displayDecimalFormatted(setting, Number(opt.totalTargetHoursGained))}`}</td>
                                                    <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(options?.targetHourlyCostRate || 0))}`}</td>
                                                    <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(options?.targetHourlyBillRate || 0))}`}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <button className={styles.selectOptionBtn} onClick={() => handleSelectOption(opt)}>
                                            Select this option
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Global errors */}
                {globalErrors.length > 0 && (
                    <div className={styles.globalErrorBanner}>
                        {globalErrors.map((e, i) => <p key={i}>{e.message}</p>)}
                    </div>
                )}

                {validation && Math.abs(validation.preview?.totalCostDelta) >= 0.01 && (
                    <div className={styles.costWarningBanner}>
                        This reshuffle will change the project forecasted cost by{" "}
                        {getAccountCurrency(setting)} {displayDecimalFormatted(setting, Number(validation.preview.totalCostDelta))}
                    </div>
                )}
                {validation && Math.abs(validation.preview?.totalBillDelta) >= 0.01 && (
                    <div className={styles.costWarningBanner}>
                        This reshuffle will change the project forecasted bill by{" "}
                        {getAccountCurrency(setting)} {displayDecimalFormatted(setting, Number(validation.preview.totalBillDelta))}
                    </div>
                )}

                {/* Editable grid */}
                {editableItems.length === 0 ? (
                    !directModalLoading && <div className={styles.emptyGrid}>Select a system option.</div>
                ) : (
                    <div className={styles.editableGrid}>
                        <div className={styles.gridHeader}>
                            <span>Donor</span>
                            <span>Donor Reduced Hrs</span>
                            <span>Target Added Hrs</span>
                            <span></span>
                        </div>
                        {editableItems.map((item) => {
                            return (
                                <div key={item.tempId} className={styles.gridRow}>
                                    <div className={styles.gridCell}>
                                        <div className={styles.donorSelectWrapper}>
                                            <span>{item.donorResource ? renderUserHoverCell(item.donorResource, 18) : "No donor selected"}</span>
                                        </div>
                                    </div>

                                    <div className={styles.gridCell}>
                                        <span>{displayDecimalFormatted(setting, Number(item.donorHoursReduced))}</span>
                                    </div>

                                    <div className={styles.gridCell}>
                                        <span>{displayDecimalFormatted(setting, Number(item.targetHoursAdded))}</span>
                                    </div>
                                    <div className={`${styles.gridCell}`}>
                                        <X className="cursor-pointer" color="#dc2626" size={16} onClick={() => removeEditableItem(item.tempId)} />
                                    </div>
                                </div>
                            );
                        })}
                        {/* {editableItems.filter((item) => item.tempId?.startsWith('new-') || item.tempId?.startsWith('opt-')).map((item) => {
                            return (
                                <div key={item.tempId} className={styles.gridRow}>
                                    <div className={styles.gridCell}>
                                        <div className={styles.donorSelectWrapper}>
                                            <span>hehe</span>
                                        </div>
                                    </div>

                                    <div className={styles.gridCell}>
                                        <span>{displayDecimalFormatted(setting, Number(item.donorHoursReduced))}</span>
                                    </div>

                                    <div className={styles.gridCell}>
                                        <span>{displayDecimalFormatted(setting, Number(item.targetHoursAdded))}</span>
                                    </div>
                                    <div className={`${styles.gridCell}`}>
                                        <X className="cursor-pointer" color="#dc2626" size={16} onClick={() => removeEditableItem(item.tempId)} />
                                    </div>
                                </div>
                            );
                        })} */}
                    </div>
                    // <div className={styles.editableGrid}>
                    //     <div className={styles.gridHeader}>
                    //         <span>Donor</span>
                    //         <span>Donor Reduced Hrs</span>
                    //         <span>Donor Reduced Hrs</span>
                    //         <span>Donor Cost/Bill Rate</span>
                    //         <span>Target Cost/Bill Rate</span>
                    //         <span></span>
                    //     </div>
                    //     {editableItems.map((item) => {
                    //         const rowErrors = getRowErrors(item.donorResource?.id);
                    //         const rowWarnings = getRowWarnings(item.donorResource?.id);
                    //         const resourceData = getResourceDataFromPool(item.donorResource?.id);
                    //         const isNew = item.id?.startsWith('new-') ?? false;
                    //         return (
                    //             <div key={item.tempId} className={styles.gridRow}>
                    //                 <div className={styles.gridCell}>
                    //                     <div className={styles.donorSelectWrapper}>
                    //                         {
                    //                             isNew ?
                    //                                 <DropDown
                    //                                     options={resourcesPool.map((r) => (
                    //                                         {
                    //                                             name: `${r.resource?.name} (${displayDecimalFormatted(setting, Number(r.availableFormattedHours || 0))} hrs)`,
                    //                                             value: r.resource?.id,
                    //                                         }
                    //                                     ))}
                    //                                     defaultVal={resourceData?.resource ? renderUserHoverCell(resourceData?.resource, 18) : ""}
                    //                                     onChange={(e) => {
                    //                                         const sel = e ? resourcesPool.find((r) => r.resource?.id === e) : null;
                    //                                         updateEditableItem(item.tempId, "donorResource", sel?.resource || null);
                    //                                     }}
                    //                                     placeholder="Select Donor"
                    //                                     dropDownwd={"flex-1"}
                    //                                 />
                    //                                 :
                    //                                 <span>{item.donorResource ? renderUserHoverCell(item.donorResource, 18) : "No donor selected"}</span>
                    //                         }
                    //                     </div>
                    //                 </div>

                    //                 <div className={styles.gridCell}>
                    //                     <Tooltip content={"Available hours to fund this transfer"} relationship="label" positioning="above-start" withArrow>
                    //                         <p
                    //                             className={`
                    //                                         text-center py-[9px] rounded-md me-2 Input_wrapper position-relative`}
                    //                             style={{
                    //                                 backgroundColor: Number(resourceData?.availableFormattedHours || 0) >= Number(item.donorHoursReduced || 0) ? "#107c101f" : "#dc26261f",
                    //                                 color: Number(resourceData?.availableFormattedHours || 0) >= Number(item.donorHoursReduced || 0) ? "#107c10" : "#dc2626",
                    //                             }}
                    //                         >
                    //                             {`${displayDecimalFormatted(
                    //                                 setting,
                    //                                 Number(resourceData?.availableFormattedHours || 0)
                    //                             )}`}
                    //                         </p>
                    //                     </Tooltip>

                    //                 </div>

                    //                 <div className={styles.gridCell}>
                    //                     {isNew ?
                    //                         <Input
                    //                             inputwd="flex-1"
                    //                             value={item.targetHoursAdded}
                    //                             min={0.1}
                    //                             type="number"
                    //                             handleChange={(value) => updateEditableItem(item.tempId, "targetHoursAdded", value)}
                    //                             placeholder=""
                    //                         />
                    //                         :
                    //                         <span>{displayDecimalFormatted(setting, Number(item.targetHoursAdded))}</span>
                    //                     }
                    //                 </div>
                    //                 <div className={`${styles.gridCell}`}>
                    //                     <X className="cursor-pointer" color="#dc2626" size={16} onClick={() => removeEditableItem(item.tempId)} />
                    //                 </div>
                    //                 {rowErrors.length > 0 && rowErrors.map((error, index) => (

                    //                     <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.errorBanner}`}>
                    //                         {error.message}{error.suggestedHours != null ? ` Max: ${error.suggestedHours} hrs` : ""}
                    //                     </div>
                    //                 ))}
                    //                 {Number(resourceData?.availableFormattedHours || 0) < Number(item.donorHoursReduced || 0) &&

                    //                     <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.infoBanner} mt-1`}>
                    //                         {`Available hours for this resource is ${displayDecimalFormatted(
                    //                             setting,
                    //                             Number(resourceData?.availableFormattedHours || 0)
                    //                         )} hrs. Consider reducing donor hours or selecting a different donor.`}
                    //                     </div>

                    //                 }
                    //                 {rowWarnings.length > 0 && rowWarnings.map((warning, index) => (

                    //                     <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.infoBanner} mt-1`}>
                    //                         {warning.message}{warning.suggestedHours != null ? ` Max: ${warning.suggestedHours} hrs` : ""}
                    //                     </div>
                    //                 ))}

                    //             </div>
                    //         );
                    //     })}
                    // </div>
                )}
                {/* {
                    (!editableItems || editableItems.length === 0) &&
                    <button className={styles.addRowBtn} onClick={addEditableItem}>+ Add Row</button>
                } */}


                {/* Notes */}
                <div className={`${styles.formGroup} mt-3`}>
                    <label className={styles.formLabel}>Notes</label>
                    <RichTextEditor
                        content={notes}
                        onChange={(value) => { setNotes(value) }}
                        loading={directModalLoading}
                    />
                </div>

                {/* Attachments */}
                <div className={`${styles.formGroup} mt-3`}>
                    <EntityFileUpload
                        entityId={requestId}
                        entityType="ReshuffleRequest"
                        attachments={attachments}
                        onAttachmentsChange={() => {
                            if (requestId) {
                                reshuffleservice
                                    .getSingleReshuffleRequest(projectID, requestId)
                                    .then((reshuffleRequestData) => {
                                        setAttachments(reshuffleRequestData.attachments);
                                    })
                                    .catch((error) => {
                                        console.error(
                                            "Error fetching request data:",
                                            error
                                        );
                                    });
                            }
                        }}
                        disabled={!requestId}
                    />
                </div>
            </div>
        );
    };

    const fetchResourcePool = () => {
        const params = {
            targetResourceId: targetResource?.id,
            year: periodYear,
            month: periodMonth
        };

        reshuffleservice.getReshufflePool(projectID, params)
            .then((res) => setResourcesPool(res))
            .catch((error) => {
                setResourcesPool([]);
            });
    };

    // ── Main form body (dispatched to Table.jsx modal) ──────────────────────────
    const formBody = () => {
        if (modalMode === "edit") return renderEditMode();
        switch (wizardStep) {
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            default: return renderStep1();
        }
    };

    // ── View modal body ─────────────────────────────────────────────────────────
    const renderViewBody = () => {
        if (directModalLoading) {
            return <div className={styles.loadingState}><Spinner animation="border" /></div>;
        }
        if (!viewDetail) return null;

        const totalCostDelta = viewDetail.items.reduce((acc, item) => acc + item.costDelta, 0);
        const totalBillDelta = viewDetail.items.reduce((acc, item) => acc + item.billDelta, 0);
        return (
            <div className={styles.viewContainer}>
                <div className={styles.summaryHeader}>
                    <div className={styles.summaryCell}>
                        <p className={styles.summaryTitle}>
                            {renderUserHoverCell(viewDetail.targetResource)}
                        </p>
                    </div>
                    <div className={styles.summaryCell}>
                        <p className={styles.summarySubtitle}>
                            <span className={styles.summaryStatusBadge}>
                                {`${formatMonthYear(viewDetail.year, viewDetail.month)} • ${displayDecimalFormatted(setting, viewDetail.requestedHours)} hrs`}
                            </span>
                        </p>
                    </div>

                    <div className={styles.summaryCell}>
                        {renderDeltaPill(totalCostDelta, false, true)}
                    </div>
                    <div className={styles.summaryCell}>
                        {renderDeltaPill(totalBillDelta, true, true)}
                    </div>

                    <div className={styles.summaryCell}>
                        {viewDetail.status !== undefined && (
                            <span className={styles.summaryStatusBadge}>
                                {getReshuffleStatusLabel(viewDetail.status)}
                            </span>
                        )}
                    </div>
                </div>

                {viewDetail.items?.length > 0 && (
                    <table className={styles.itemsTable}>
                        <thead>
                            <tr>
                                <th>Donor</th>
                                <th>Donor Reduced Hrs</th>
                                <th>Target Added Hrs</th>
                                <th>Donor Cost/Bill Rate</th>
                                <th>Target Cost/Bill Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {viewDetail.items.map((item, i) => (
                                <tr key={i}>
                                    <td>{renderUserHoverCell(item.donorResource, 18)}</td>
                                    <td>{displayDecimalFormatted(setting, item.donorHoursReduced)}</td>
                                    <td>{displayDecimalFormatted(setting, item.targetHoursAdded)}</td>
                                    <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, item.donorForecastHourlyCostRate)} / ${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, item.donorForecastHourlyBillRate)}`}</td>
                                    <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, item.targetForecastHourlyCostRate)} / ${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, item.targetForecastHourlyBillRate)}`}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {
                    viewDetail.notes && (
                        <>
                            <div className={styles.sectionTitle}>Notes</div>
                            <div className={styles.summaryNotes}>
                                <SafeHtml
                                    html={viewDetail.notes}
                                />
                            </div>
                        </>
                    )
                }
                {
                    viewDetail.reviewNotes && (
                        <>
                            <div className={styles.sectionTitle}>Review Notes</div>
                            <div className={styles.summaryNotes}>
                                <SafeHtml
                                    html={viewDetail.reviewNotes}
                                />
                            </div>
                        </>
                    )
                }

                {/* Attachments */}
                {
                    viewDetail.attachments && viewDetail.attachments.length > 0 && (
                        <>
                            <div className={styles.sectionTitle}>Attachments</div>
                            <div className={styles.summaryNotes}>
                                <EntityFileUpload
                                    label=""
                                    entityId={viewDetail.id}
                                    entityType="ReshuffleRequest"
                                    attachments={viewDetail.attachments}
                                    onAttachmentsChange={() => { }}
                                    disabled={true}
                                />
                            </div>
                        </>

                    )
                }

            </div>
        );
    };

    // ── Review (approve/reject) body ────────────────────────────────────────────
    const renderReviewBody = () => (
        <div className={styles.reviewContainer}>
            <div className={styles.summaryHeader}>
                <div className={styles.summaryCell}>
                    <p className={styles.summaryTitle}>
                        {renderUserHoverCell(directModalRow?.targetResource)}
                    </p>
                </div>
                <div className={styles.summaryCell}>
                    <p className={styles.summarySubtitle}>
                        <span className={styles.summaryStatusBadge}>
                            {`${formatMonthYear(directModalRow?.year, directModalRow?.month)} • ${displayDecimalFormatted(setting, directModalRow?.requestedHours)} hrs`}
                        </span>
                    </p>
                </div>
                <div className={styles.summaryCell}>
                    {directModalRow?.status !== undefined && (
                        <span className={styles.summaryStatusBadge}>
                            {getReshuffleStatusLabel(directModalRow?.status)}
                        </span>
                    )}
                </div>
            </div>
            <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                    Review Notes{directModalMode === "reject" && <span className={styles.required}> *</span>}
                </label>
                <RichTextEditor
                    content={reviewNotes}
                    onChange={(value) => { setReviewNotes(value) }}
                    loading={directModalLoading}
                />
                {reviewNotesError && <p className={styles.errorText}>{reviewNotesError}</p>}
            </div>
        </div>
    );

    // ── Summary modal body ──────────────────────────────────────────────────────
    const renderSummaryBody = () => {
        if (directModalLoading) return <div className={styles.loadingState}><Spinner animation="border" /></div>;
        if (!summaryData) return null;
        return <ReshuffleSummaryPreview preview={summaryData} />;
    };

    const handleSearchTrigger = (searchTerm) => {
        if ((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;

        let pageNumber = pageNum;
        if (!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)) {
            setData([]); //clear existing data
            pageNumber = 1; //reset to first page
        }

        setTableSearch(searchTerm);
        fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);
    };

    const handleSearch = (filteredData) => {
        setData(filteredData);
    };

    const getModalWidth = () => {
        if (modalMode === "edit") return "1000px";
        if (wizardStep === 2 || wizardStep === 3) return "1300px";
        if (wizardStep === 1) return "500px";
        return null;
    };
    // ─────────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────────
    return (
        <ColumnConfigProvider tableName="Project-Reshuffle-Request">
            <div className="listingScreen position-relative">
                <Table
                    type="Reshuffle Request"
                    rows={data || []}
                    body={formBody()}
                    searchHandler={handleSearch}
                    saveBtnLabel={getSaveBtnLabel()}
                    cancelBtnLabel="Cancel"
                    setPageNum={setPageNum}
                    perPageItems={perPageItems}
                    setIsCreate={handleCreate}
                    setIsEdit={() => setModalMode("edit")}
                    setData={setData}
                    parentType="Project"
                    loading={loading}
                    modalLoading={pageLoading}
                    pagination={pagination}
                    isCreate={modalMode === "create"}
                    handleSave={handleSave}
                    handleDelete={handleDelete}
                    handleCancel={resetForm}
                    setSelectedRow={handleRowSelect}
                    handleAction={handleAction}
                    projectId={projectID}
                    triggerSearch={handleSearchTrigger}
                    isModalSaveBtnDisabled={isSaveBtnDisabled()}
                    modalWidth={getModalWidth()}
                />

                {/* ── View Modal ──────────────────────────────────────────────────── */}
                {showDirectModal && directModalMode === "view" && (
                    <Modal
                        title="Request Details"
                        body={renderViewBody()}
                        handleCancel={closeDirectModal}
                        handleSave={null}
                        footer={false}
                        size="large"
                        width={"1300px"}
                    />
                )}

                {/* ── Summary Modal ───────────────────────────────────────────────── */}
                {showDirectModal && directModalMode === "summary" && (
                    <Modal
                        title="Request Summary"
                        body={renderSummaryBody()}
                        handleCancel={closeDirectModal}
                        handleSave={null}
                        footer={false}
                        size="large"
                        width={"1300px"}
                    />
                )}

                {/* ── Approve Modal ───────────────────────────────────────────────── */}
                {showDirectModal && directModalMode === "approve" && (
                    <Modal
                        title="Approve Request"
                        body={renderReviewBody()}
                        handleCancel={closeDirectModal}
                        handleSave={handleApproveConfirm}
                        saveBtnLabel="Approve"
                        cancelBtnLabel="Cancel"
                        isLoading={pageLoading}
                        size="small"
                    />
                )}

                {/* ── Reject Modal ────────────────────────────────────────────────── */}
                {showDirectModal && directModalMode === "reject" && (
                    <Modal
                        title="Reject Request"
                        body={renderReviewBody()}
                        handleCancel={closeDirectModal}
                        handleSave={handleRejectConfirm}
                        saveBtnLabel="Reject"
                        cancelBtnLabel="Cancel"
                        isLoading={pageLoading}
                        size="small"
                    />
                )}
            </div>
        </ColumnConfigProvider>
    );
};

export default ReshuffleRequest;
